import { memo, type ReactNode } from "react";
import { Link } from "./core/Link";

/**
 * Parses string as markdown for
 * italic (e.g. _italics_)
 * bold (e.g. *bold*)
 * links (e.g. [example](https://example.com)).
 *
 * @param props - children must be a string
 */
export const MarkdownLite = memo(
  ({ children: childrenProp }: { children?: string | null }) => {
    const children = childrenProp ?? "";
    const result = Array<ReactNode>();
    let escape = false;

    for (let index = 0; index < children.length; index++) {
      const char = children[index];
      if (!escape && char === "\\") {
        escape = true;
        continue;
      }

      const state = escape ? null : SimpleStringFormattingMap[char] ?? null;

      if (escape) {
        escape = false;
      }

      if (state == null) {
        pushToNodeArray(result, char);
      } else {
        const lookAheadSetLength = getLookAheadSetLengthForCaptureGroup(
          children.slice(index),
          state.capture
        );

        if (lookAheadSetLength < 1) {
          pushToNodeArray(result, char);
          continue;
        }

        pushToNodeArray(
          result,
          <state.Component
            key={index}
            input={children.slice(index, index + lookAheadSetLength + 1)}
          />
        );

        index += lookAheadSetLength;
      }
    }

    return result;
  }
);

const pushToNodeArray = (
  nodeArray: Array<ReactNode>,
  addition: ReactNode
): Array<ReactNode> => {
  const top = nodeArray[nodeArray.length - 1];
  if (typeof top === "string" && typeof addition === "string") {
    nodeArray[nodeArray.length - 1] = top + addition;
  } else {
    nodeArray.push(addition);
  }

  return nodeArray;
};

type SimpleStringFormattingStateType =
  | "ITALIC"
  | "BOLD"
  | "LINK"
  | "LINE_BREAK";
type SimpleStringFormattingState = {
  capture: Array<string>;
  type: SimpleStringFormattingStateType;
  Component: (props: { input: string }) => ReactNode;
};

const SimpleStringFormattingMap: Record<string, SimpleStringFormattingState> = {
  "*": {
    capture: ["*", "*"],
    type: "BOLD",
    Component: ({ input }) => (
      <strong className="font-bold">
        <MarkdownLite>{input.slice(1, -1)}</MarkdownLite>
      </strong>
    ),
  },
  _: {
    capture: ["_", "_"],
    type: "ITALIC",
    Component: ({ input }) => (
      <em className="italic [font-synthesis:style] mr-[.1em]">
        <MarkdownLite>{input.slice(1, -1)}</MarkdownLite>
      </em>
    ),
  },
  "[": {
    capture: ["[", "](", ")"],
    type: "LINK",
    Component: ({ input }) => {
      const groups = /\[(?<content>.*)\]\((?<href>.*)\)/.exec(input)?.[
        "groups"
      ];

      if (!groups || !groups.content || !groups.href) {
        return input;
      }

      return (
        <Link href={groups.href} className="underline">
          <MarkdownLite>{groups.content}</MarkdownLite>
        </Link>
      );
    },
  },
  "<": {
    capture: ["<br/>"],
    type: "LINE_BREAK",
    Component: () => <br />,
  },
};

const getLookAheadSetLengthForCaptureGroup = (
  input: string,
  capture: Array<string>
): number => {
  let captureItemIndex = 0;
  const captureItem = () => capture[captureItemIndex];

  for (let index = 0; index < input.length; index++) {
    const group = input.slice(index, index + captureItem().length);

    if (captureItem() === group) {
      index += captureItem().length - 1;
      captureItemIndex++;
      if (captureItemIndex > capture.length - 1) {
        return index;
      }
    }
  }

  return -1;
};
